import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Triggered by the response interceptor below when a 401 is received and refresh failed.
export function dispatchUnauthorized() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:logout'));
  }
}

// Notifies AuthContext to update its in-memory token after a silent refresh,
// so UI state doesn't go stale even though localStorage was already updated.
function dispatchTokenRefreshed(token: string) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth:token-refreshed', { detail: { token } }));
  }
}

// Serializes concurrent refresh attempts so a burst of parallel 401s
// (e.g. several requests firing right as the token expires) triggers one refresh, not several.
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) return null;

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_URL}/auth/refresh`, { refreshToken: storedRefreshToken })
      .then((response) => {
        const tokens = response.data?.data?.tokens;
        if (!tokens?.accessToken) return null;
        localStorage.setItem('token', tokens.accessToken);
        if (tokens.refreshToken) {
          localStorage.setItem('refreshToken', tokens.refreshToken);
        }
        dispatchTokenRefreshed(tokens.accessToken);
        return tokens.accessToken as string;
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// Handle 401 responses: try a silent token refresh once before logging out,
// so an expired access token doesn't nuke in-progress work on every background request.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isRefreshCall = originalRequest?.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && !isRefreshCall && !originalRequest?._retriedAfterRefresh) {
      originalRequest._retriedAfterRefresh = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
    }

    // The server refused an action because the current Terms have not been accepted: have the app show the prompt.
    if (error.response?.status === 403 && error.response?.data?.code === 'TERMS_NOT_ACCEPTED') {
      window.dispatchEvent(new Event('auth:terms-required'));
    }

    if (error.response?.status === 401) {
      console.error('[Auth] Unauthorized request:', error.config?.method?.toUpperCase(), error.config?.url);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      dispatchUnauthorized();
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data: { email: string; password: string; firstName: string; lastName: string; phone?: string }) =>
    api.post('/auth/register', data),
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  verifyMfa: (data: { tempToken: string; token: string }) =>
    api.post('/auth/mfa/verify', data),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword: (data: { token: string; newPassword: string }) =>
    api.post('/auth/reset-password', data),
  acceptTerms: (version: string) =>
    api.post('/auth/accept-terms', { version }),
  declineTerms: (version: string) =>
    api.post('/auth/decline-terms', { version }),
  completeProfile: (data: Record<string, unknown>) =>
    api.post('/auth/complete-profile', data),
  getProfile: () =>
    api.get('/users/me'),
  updateProfile: (data: any) =>
    api.put('/users/me', data),
  deleteMe: (data: { password: string }) =>
    api.delete('/users/me', { data }),
};

// Items API
export const itemsApi = {
  getAll: (params?: any) =>
    api.get('/items', { params }),
  getById: (id: string) =>
    api.get(`/items/${id}`),
  getMyItems: (params?: any) =>
    api.get('/items/me', { params }),
  create: (data: any) =>
    api.post('/items', data),
  update: (id: string, data: any) =>
    api.put(`/items/${id}`, data),
  delete: (id: string) =>
    api.delete(`/items/${id}`),
  getShippingQuote: (id: string) =>
    api.get(`/items/${id}/shipping-quote`),
};

// Rentals API
export const rentalsApi = {
  getAll: (params?: any) =>
    api.get('/rentals', { params }),
  getDashboardStats: () =>
    api.get('/rentals/dashboard-stats'),
  getEarnings: () =>
    api.get('/rentals/earnings'),
  getById: (id: string) =>
    api.get(`/rentals/${id}`),
  create: (data: any) =>
    api.post('/rentals', data),
  approve: (id: string, data?: { fulfillmentType?: 'pickup' | 'doordash' | 'shipping'; pickupLocationLabel?: string; renterPaysShipping?: boolean; rateId?: string }) =>
    api.post(`/rentals/${id}/approve`, data),
  decline: (id: string, reason: string) =>
    api.post(`/rentals/${id}/decline`, { reason }),
  cancel: (id: string) =>
    api.post(`/rentals/${id}/cancel`),
  changeFulfillmentType: (id: string, data: { fulfillmentType: 'pickup' | 'doordash' | 'shipping'; pickupLocationLabel?: string; renterPaysShipping?: boolean }) =>
    api.patch(`/rentals/${id}/fulfillment-type`, data),
  changeShippingLocation: (id: string, data: { shippingLocationLabel: string }) =>
    api.patch(`/rentals/${id}/shipping-location`, data),
  getShippingQuote: (id: string, pickupLocationLabel?: string) =>
    api.get(`/rentals/${id}/shipping-quote`, { params: pickupLocationLabel ? { pickupLocationLabel } : undefined }),
  getShippingRates: (id: string, pickupLocationLabel?: string) =>
    api.get(`/rentals/${id}/shipping-rates`, { params: pickupLocationLabel ? { pickupLocationLabel } : undefined }),
  purchaseShippingLabel: (id: string, data: { rateId: string }) =>
    api.post(`/rentals/${id}/purchase-shipping-label`, data),
  refundShippingLabel: (id: string) =>
    api.post(`/rentals/${id}/shipping-label/refund`),
  pickup: (id: string, photoUrl: string) =>
    api.post(`/rentals/${id}/pickup`, { photoUrl }),
  return: (id: string, photoUrl?: string, returnTrackingNumber?: string, returnCarrier?: string) =>
    api.post(`/rentals/${id}/return`, { photoUrl, returnTrackingNumber, returnCarrier }),
  confirmReturn: (id: string) =>
    api.post(`/rentals/${id}/confirm-return`),
  getMessages: (id: string) =>
    api.get(`/rentals/${id}/messages`),
  sendMessage: (id: string, content: string) =>
    api.post(`/rentals/${id}/messages`, { content }),
  requestExtension: (id: string, newEndDate: string) =>
    api.post(`/rentals/${id}/extend`, { newEndDate }),
  approveExtension: (id: string) =>
    api.post(`/rentals/${id}/extend/approve`),
  declineExtension: (id: string) =>
    api.post(`/rentals/${id}/extend/decline`),
  cancelExtensionRequest: (id: string) =>
    api.post(`/rentals/${id}/extend/cancel`),
  createReview: (id: string, data: { rating: number; content?: string; wouldRentAgain?: boolean }) =>
    api.post(`/rentals/${id}/reviews`, data),
  reportProblem: (id: string) =>
    api.post(`/rentals/${id}/report-problem`),
};

// Cart API
export const cartApi = {
  get: () => api.get('/cart'),
  addItem: (data: { itemId: string; transactionType: string; startDate?: string; endDate?: string; deliveryOption?: string }) =>
    api.post('/cart/items', data),
  removeItem: (itemId: string) => api.delete(`/cart/items/${itemId}`),
  clear: () => api.delete('/cart/clear'),
  getSummary: () => api.get('/cart/summary'),
};

// Notifications API
export const notificationsApi = {
  getAll: (params?: any) =>
    api.get('/notifications', { params }),
  getUnreadCount: () =>
    api.get('/notifications/unread-count'),
  markAsRead: (id: string) =>
    api.patch(`/notifications/${id}/read`),
  markAllAsRead: () =>
    api.post('/notifications/mark-all-read'),

  // Web push. getPushPublicKey returns { publicKey: string | null } -- null means the
  // server has no VAPID keys configured, i.e. push can't work in this environment.
  getPushPublicKey: () =>
    api.get('/notifications/push/public-key'),
  subscribeToPush: (subscription: unknown) =>
    api.post('/notifications/push/subscribe', subscription),
  unsubscribeFromPush: (endpoint: string) =>
    api.delete('/notifications/push/subscribe', { data: { endpoint } }),
  sendTestPush: () =>
    api.post('/notifications/push/test'),
};

// Messages API -- cross-rental conversation list (find your way back to a thread
// without having to remember which rental it was attached to). Sending/reading
// within a single thread stays on rentalsApi (/rentals/:id/messages).
export const messagesApi = {
  getConversations: () =>
    api.get('/messages/conversations'),
  getUnreadCount: () =>
    api.get('/messages/unread-count'),
  getLatestUnread: () =>
    api.get('/messages/latest-unread'),
};

// Payment API
export const paymentApi = {
  createIntent: (rentalId: string) =>
    api.post('/payments/create-intent', { rentalId }),
  getStatus: (rentalId: string) =>
    api.get(`/payments/${rentalId}`),
  createConnectAccount: () =>
    api.post('/payments/connect-account'),
  getConnectStatus: () =>
    api.get('/payments/connect-status'),
  createExtensionIntent: (rentalId: string) =>
    api.post('/payments/create-extension-intent', { rentalId }),
  getExtensionStatus: (rentalId: string) =>
    api.get(`/payments/${rentalId}/extension`),
};

// Upload API
export const uploadApi = {
  image: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  images: (files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));
    return api.post('/upload/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  avatar: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post('/upload/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Geocoding — resolves a real street address to coordinates (OpenStreetMap Nominatim,
// proxied server-side). Used as a more accurate fallback to the static city-centroid lookup.
export const geocodeApi = {
  resolve: (data: { address: string; city: string; state: string; zip?: string }) =>
    api.post('/geocode', data),
};

// Temp images API (used for inspection/pickup photos staged before a fulfillment record exists)
export const imagesApi = {
  uploadTemp: (file: File) => {
    const formData = new FormData();
    formData.append('images', file);
    return api.post('/images/temp', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Rotation is clockwise: 90 = turn right, 270 = turn left. The server saves the result under a
  // new id (and so a new url) and returns it; the caller swaps it in for the old photo.
  rotateTemp: (imageId: string, degrees: 90 | 180 | 270) =>
    api.post(`/images/temp/${imageId}/rotate`, { degrees }),
  rotateItemImage: (itemId: string, imageId: string, degrees: 90 | 180 | 270) =>
    api.post(`/images/${itemId}/${imageId}/rotate`, { degrees }),
};

// Categories API
// Admin-configurable app settings (generic key/value -- not specific to any one feature)
export const settingsApi = {
  getAll: () =>
    api.get('/settings'),
  update: (key: string, data: { value: string; description?: string }) =>
    api.put(`/settings/${key}`, data),
};

export const categoriesApi = {
  getAll: () =>
    api.get('/categories'),
  create: (data: { name: string; sortOrder?: number }) =>
    api.post('/categories', data),
  update: (id: string, data: { name?: string; sortOrder?: number; isActive?: boolean }) =>
    api.patch(`/categories/${id}`, data),
  deactivate: (id: string) =>
    api.delete(`/categories/${id}`),
};

// Admin API
export const adminApi = {
  getStats: () =>
    api.get('/admin/stats'),
  getUsers: (params?: any) =>
    api.get('/admin/users', { params }),
  verifyUser: (id: string) =>
    api.put(`/admin/users/${id}/verify`),
  toggleUser: (id: string, isActive: boolean) =>
    api.put(`/admin/users/${id}/suspend`, { isActive }),
  getListings: (params?: any) =>
    api.get('/admin/listings', { params }),
  deactivateListing: (id: string) =>
    api.put(`/admin/listings/${id}/deactivate`),
  getRentals: (params?: any) =>
    api.get('/admin/rentals', { params }),
  getRental: (id: string) =>
    api.get(`/admin/rentals/${id}`),
  updateRentalStatus: (id: string, data: { status: string; reason: string }) =>
    api.put(`/admin/rentals/${id}/status`, data),
  refundRental: (id: string, data: { amount: number; reason: string }) =>
    api.post(`/admin/rentals/${id}/refund`, data),
  getRevenue: () =>
    api.get('/admin/revenue'),
  getAuditLog: (params?: any) =>
    api.get('/admin/audit-log', { params }),
  getUser: (id: string) =>
    api.get(`/admin/users/${id}`),
  setUserRole: (id: string, role: 'ADMIN' | 'USER') =>
    api.put(`/admin/users/${id}/role`, { role }),
  banUser: (id: string, reason: string) =>
    api.post(`/admin/users/${id}/ban`, { reason }),
  reinstateUser: (id: string) =>
    api.post(`/admin/users/${id}/reinstate`),
  watchUser: (id: string, watched: boolean, note?: string) =>
    api.put(`/admin/users/${id}/watch`, { watched, note }),
  getAnalytics: (days: number) =>
    api.get('/admin/analytics', { params: { days } }),
  getActivity: (params?: any) =>
    api.get('/admin/activity', { params }),
  // Security: alerts, flags, sweeps, listing blocks
  getSecurityEvents: (params?: any) =>
    api.get('/admin/security/events', { params }),
  acknowledgeEvent: (id: string) =>
    api.put(`/admin/security/events/${id}/acknowledge`),
  acknowledgeAllEvents: () =>
    api.post('/admin/security/events/acknowledge-all'),
  getInbox: () =>
    api.get('/admin/security/inbox'),
  getIncomingMail: () =>
    api.get('/admin/inbox/incoming'),
  checkInbox: () =>
    api.post('/admin/security/inbox/check'),
  getInboxMessages: (params?: any) =>
    api.get('/admin/inbox/messages', { params }),
  getInboxMessage: (id: string) =>
    api.get(`/admin/inbox/messages/${id}`),
  inboxAction: (id: string, action: 'pickup' | 'release' | 'complete' | 'reopen') =>
    api.post(`/admin/inbox/messages/${id}/${action}`),
  addInboxNote: (id: string, text: string) =>
    api.post(`/admin/inbox/messages/${id}/notes`, { text }),
  getReports: (params?: any) =>
    api.get('/admin/reports', { params }),
  updateReport: (id: string, status: 'OPEN' | 'RESOLVED' | 'DISMISSED', note?: string) =>
    api.put(`/admin/reports/${id}`, { status, note }),
  getFlags: (params?: any) =>
    api.get('/admin/security/flags', { params }),
  updateFlag: (id: string, status: 'OPEN' | 'DISMISSED' | 'ACTIONED') =>
    api.put(`/admin/security/flags/${id}`, { status }),
  removeFlaggedPhoto: (id: string) =>
    api.post(`/admin/security/flags/${id}/remove-photo`),
  sweepListings: () =>
    api.post('/admin/security/sweep/listings'),
  sweepImages: (limit?: number) =>
    api.post('/admin/security/sweep/images', null, { params: { limit } }),
  blockListing: (id: string, reason: string) =>
    api.post(`/admin/listings/${id}/block`, { reason }),
  approveListing: (id: string) =>
    api.post(`/admin/listings/${id}/approve`),
  confirmListingBlock: (id: string) =>
    api.post(`/admin/listings/${id}/confirm-block`),
  // Employees and mail
  getEmployees: () =>
    api.get('/admin/employees'),
  createEmployee: (data: { firstName: string; lastName?: string; localPart: string; makeAdmin?: boolean; createMailbox?: boolean; permissions?: string[] }) =>
    api.post('/admin/employees', data),
  setEmployeePermissions: (userId: string, permissions: string[]) =>
    api.put(`/admin/employees/${userId}/permissions`, { permissions }),
  resendInvite: (userId: string) =>
    api.post(`/admin/employees/${userId}/invite`),
  offboardEmployee: (userId: string, deleteMailbox: boolean) =>
    api.post(`/admin/employees/${userId}/offboard`, { deleteMailbox }),
  getMailboxes: () =>
    api.get('/admin/mail/mailboxes'),
  resetMailboxPassword: (localPart: string) =>
    api.put(`/admin/mail/mailboxes/${localPart}/password`, {}),
  deleteMailbox: (localPart: string, force = false) =>
    api.delete(`/admin/mail/mailboxes/${localPart}`, { params: { force } }),
  addMailAlias: (name: string, target: string) =>
    api.post('/admin/mail/aliases', { name, target }),
  removeMailAlias: (name: string) =>
    api.delete(`/admin/mail/aliases/${name}`),
};

// Reports: a user flags a listing or another person to the admins
export const reportsApi = {
  create: (data: { targetType: 'LISTING' | 'USER'; targetId: string; category: string; details?: string }) =>
    api.post('/reports', data),
};

// Fulfillments API
export const fulfillmentsApi = {
  create: (data: any) =>
    api.post('/fulfillments', data),
  get: (id: string) =>
    api.get(`/fulfillments/${id}`),
  getByRental: (rentalId: string) =>
    api.get(`/fulfillments/rental/${rentalId}`),
  updateStatus: (id: string, status: string, metadata?: any) =>
    api.patch(`/fulfillments/${id}/status`, { status, metadata }),
  confirmHandshake: (id: string) =>
    api.post(`/fulfillments/${id}/handshake`),
  verifyQR: (id: string, qrCode: string) =>
    api.post(`/fulfillments/${id}/verify-qr`, { qrCode }),
  getAuditLog: (id: string, params?: any) =>
    api.get(`/fulfillments/${id}/audit-log`, { params }),
  getPendingConfirmations: () =>
    api.get('/fulfillments/pending-confirmations'),
  confirmPickup: (id: string, data?: { pickupWindowStart?: string; pickupWindowEnd?: string; geofenceRadius?: number; instructions?: string; carrier?: string; trackingNumber?: string }) =>
    api.post(`/fulfillments/${id}/confirm`, data),
  notifyOwner: (id: string) =>
    api.post(`/fulfillments/${id}/notify-owner`),
  checkLocation: (id: string, latitude: number, longitude: number) =>
    api.post(`/fulfillments/${id}/check-location`, { latitude, longitude }),
  getGeofence: (id: string) =>
    api.get(`/fulfillments/${id}/geofence`),
  suggestTime: (id: string, data: { proposedWindowStart: string; proposedWindowEnd: string; message?: string }) =>
    api.post(`/fulfillments/${id}/suggest-time`, data),
  uploadPhotos: (id: string, data: { photos: string[]; type: 'pre_pickup' | 'post_pickup' | 'return'; notes?: string }) =>
    api.post(`/fulfillments/${id}/photos`, data),
  getTracking: (id: string) =>
    api.get(`/fulfillments/${id}/tracking`),
  refreshTracking: (id: string, leg: 'outbound' | 'return') =>
    api.post(`/fulfillments/${id}/tracking/${leg}/refresh`),
  markShipped: (id: string) =>
    api.post(`/fulfillments/${id}/mark-shipped`),
};
